// Display helpers and enum lists shared by every wired page.

export const inr = (n, opts = {}) => {
  const v = Number(n ?? 0);
  if (Number.isNaN(v)) return "₹0";
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: opts.decimals ?? (Number.isInteger(v) ? 0 : 2) });
};

export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const fmtDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
};

// Date with year plus time: for stamps that may be years old (a patient's
// registration), where fmtDateTime's year-less form would mislead.
export const fmtDateTimeFull = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
};

export const fmtTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
};

export const todayISO = () => new Date().toISOString().slice(0, 10);
export const monthKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
export const startOfDayISO = (dateStr) => new Date(`${dateStr}T00:00:00`).toISOString();
export const endOfDayISO = (dateStr) => new Date(`${dateStr}T23:59:59.999`).toISOString();

// "PENDING_COLLECTION" -> "Pending Collection"
export const RELATIONS = ["SELF", "SPOUSE", "MOTHER", "FATHER", "SON", "DAUGHTER", "BROTHER", "SISTER", "GRANDFATHER", "GRANDMOTHER", "OTHER"];
export const relationLabel = (r) => (r ? r.charAt(0) + r.slice(1).toLowerCase() : "");
// The words come from database enum values, some of which are spelled CENTRE
// (they cannot be renamed without a migration); what is shown uses the
// product's spelling, "Center".
export const human = (s) =>
  String(s ?? "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ")
    .replace(/\b([Cc])entre(s?)\b/g, "$1enter$2");

export const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin",
  CITY_ADMIN: "City Admin",
  HUB_ADMIN: "Hub Admin",
  CENTER_ADMIN: "Collection Center Admin",
  ONLINE_BOOKING_MANAGER: "Online Booking Manager",
  HOME_COLLECTION_COORDINATOR: "Home Collection Coordinator",
  OPERATIONS_COORDINATOR: "Operations Coordinator",
  RECEPTIONIST: "Receptionist",
  TECHNICIAN: "Technician",
  SENIOR_TECHNICIAN: "Senior Technician",
  PATHOLOGIST: "Pathologist",
  ACCOUNTS_MANAGER: "Accounts Manager",
  QUALITY_MANAGER: "Quality Manager",
  MARKETING_EXECUTIVE: "Marketing Executive",
  COLLECTION_CENTER: "Collection Center",
  FRANCHISE: "Franchise",
  MANAGEMENT: "Management / MIS",
  PURCHASE_MANAGER: "Purchase / Procurement",
  HUB_RECEIVING: "Hub Receiving",
  HOME_COLLECTION_MANAGER: "Home Collection Manager",
  PHLEBOTOMIST: "Phlebotomist",
  HOME_COLLECTION_VENDOR: "Home Collection Vendor",
  B2B_ADMIN: "B2B Admin",
  B2B_STAFF: "B2B Staff",
  B2B_ACCOUNTS: "B2B Accounts",
  RUNNER: "Runner / Pickup Executive",
  PARTNER_LAB_ADMIN: "Partner Lab Admin",
  CUSTOMER_CARE: "CRM Executive",
  MARKETING_MANAGER: "Marketing Manager",
  RADIOLOGY_HEAD: "Radiology Head",
};
export const ROLES = Object.keys(ROLE_LABELS);
export const roleLabel = (r) => ROLE_LABELS[r] || human(r);

export const ORDER_STATUSES = ["REGISTERED", "BILLED", "COLLECTED", "PROCESSING", "COMPLETED", "RELEASED", "CANCELLED"];
export const SPECIMEN_STATUSES = [
  "PENDING_COLLECTION", "COLLECTED", "BATCHED", "DISPATCHED", "RECEIVED_AT_HUB", "ACCEPTED", "REJECTED",
  "PROCESSING", "QC", "RESULT_ENTERED", "VERIFIED", "APPROVED", "RELEASED", "RETAINED", "ARCHIVED", "DISPOSED",
];
export const PAYMENT_MODES = ["CASH", "UPI", "CARD", "NETBANKING", "CHEQUE", "PAYMENT_LINK", "CORPORATE_CREDIT", "WALLET"];
export const EXPENSE_MODES = ["CASH", "UPI", "CARD", "NETBANKING", "CHEQUE", "WALLET"];
export const GENDERS = ["MALE", "FEMALE", "OTHER"];
export const REJECT_CATEGORIES = ["IDENTIFICATION", "COLLECTION", "TRANSPORT", "CONTAINER", "PHYSICAL", "OTHER"];
export const REGISTRATION_TYPES = [["OPD", "OP (out-patient)"], ["IPD", "IP (in-patient)"]];
export const LEAD_SOURCES = ["INBOUND_CALL", "OUTBOUND_CALL", "MISSED_CALL", "WHATSAPP", "WEBSITE", "MOBILE_APP", "NOVUS_CARE", "ABANDONED_CART", "RECEPTION", "WALK_IN_ENQUIRY", "FRANCHISE", "HOME_COLLECTION", "REFERRAL", "DOCTOR_REFERRAL", "CORPORATE", "GOOGLE_ADS", "META_ADS", "CAMPAIGN", "MARKETING_CAMPAIGN", "SOCIAL_MEDIA"];
// The CRM pipeline (2026-10-15), open stages first, then the exits.
export const LEAD_STATUSES = ["NEW", "CONTACT_ATTEMPTED", "CONTACTED", "FOLLOW_UP", "INTERESTED", "QUOTATION_SENT", "BOOKING_INITIATED", "PAYMENT_PENDING", "CONVERTED", "NOT_INTERESTED", "LOST", "INVALID_CONTACT", "UNSERVICEABLE", "CANCELLED", "DUPLICATE"];
export const LEAD_OPEN_STATUSES = ["NEW", "CONTACT_ATTEMPTED", "CONTACTED", "IN_PROGRESS", "FOLLOW_UP", "INTERESTED", "QUOTATION_SENT", "BOOKING_INITIATED", "PAYMENT_PENDING"];
export const LEAD_STAGE_LABEL = { NEW: "New", CONTACT_ATTEMPTED: "Contact attempted", CONTACTED: "Contacted", IN_PROGRESS: "Contacted", FOLLOW_UP: "Follow-up scheduled", INTERESTED: "Interested", QUOTATION_SENT: "Quotation sent", BOOKING_INITIATED: "Booking initiated", PAYMENT_PENDING: "Payment pending", CONVERTED: "Booked", NOT_INTERESTED: "Not interested", LOST: "Lost", INVALID_CONTACT: "Invalid contact", UNSERVICEABLE: "Unserviceable", CANCELLED: "Cancelled", DUPLICATE: "Duplicate" };
export const LEAD_SOURCE_LABEL = { INBOUND_CALL: "Inbound call", OUTBOUND_CALL: "Outbound call", MISSED_CALL: "Missed call", WHATSAPP: "WhatsApp", WEBSITE: "Website", MOBILE_APP: "App", NOVUS_CARE: "Novus Care chat", ABANDONED_CART: "Abandoned cart", RECEPTION: "Reception", WALK_IN_ENQUIRY: "Walk-in", FRANCHISE: "Franchise", HOME_COLLECTION: "Home collection", REFERRAL: "Referral", DOCTOR_REFERRAL: "Doctor referral", CORPORATE: "Corporate", GOOGLE_ADS: "Google Ads", META_ADS: "Instagram / Facebook", CAMPAIGN: "Campaign", MARKETING_CAMPAIGN: "Campaign", SOCIAL_MEDIA: "Social media" };
export const BUSINESS_UNIT_TYPES = ["OWN_HUB", "COLLECTION_CENTER", "FRANCHISE", "B2B_CENTER", "HOME_COLLECTION", "PARTNER_LAB", "CRM", "ONLINE"];
export const LEAD_OPEN = ["NEW", "IN_PROGRESS", "FOLLOW_UP", "INTERESTED"];
export const CALL_OUTCOMES = ["CONNECTED", "NO_ANSWER", "BUSY", "SWITCHED_OFF", "WRONG_NUMBER", "CALLBACK_REQUESTED", "NOT_INTERESTED"];
export const HC_STATUSES = ["BOOKED", "ASSIGNED", "EN_ROUTE", "ARRIVED", "COLLECTED", "DELIVERED_TO_HUB", "COMPLETED", "CANCELLED"];
export const CAPA_SOURCES = ["SPECIMEN_REJECTION", "IQC_FAILURE", "EQAS", "COMPLAINT", "INTERNAL_AUDIT", "CRITICAL_VALUE", "OTHER"];
export const CAPA_STATUSES = ["OPEN", "IN_PROGRESS", "VERIFIED", "CLOSED"];
export const CAPA_SEVERITIES = ["LOW", "MEDIUM", "HIGH"];
export const TARIFF_SCOPES = ["COMPANY", "CITY", "HUB", "CENTER", "B2B_PARTNER"];
export const TARIFF_STATUSES = ["DRAFT", "PENDING_APPROVAL", "APPROVED", "REJECTED", "SUPERSEDED"];
export const SETTLEMENT_PAYEES = ["FRANCHISE_CENTER", "DOCTOR", "REFERRING_CENTRE", "B2B_PARTNER", "RADIOLOGY_PARTNER"];
export const INCENTIVE_TYPES = ["REFERRAL", "ADDITIONAL_TEST", "ADDITIONAL_PACKAGE", "DAILY", "MONTHLY"];
export const WALLET_TXN_TYPES = ["TOPUP", "ORDER_PAYMENT", "REFERRAL_BONUS", "LOYALTY_REDEMPTION", "REFUND", "ADJUSTMENT"];
export const NOTIFICATION_CHANNELS = ["WHATSAPP", "SMS", "EMAIL", "PUSH"];
// Screens in the patient app a push can open (expo-router paths).
export const PUSH_LINKS = [
  { value: "", label: "Just open the app" },
  { value: "/(tabs)", label: "Home" },
  { value: "/(tabs)/tests", label: "Tests & packages" },
  { value: "/cart", label: "Cart" },
  { value: "/reports", label: "Reports" },
  { value: "/offers", label: "Offers" },
  { value: "/wallet", label: "Wallet & referrals" },
  { value: "/prescription", label: "Upload a prescription" },
  { value: "/(tabs)/care", label: "Novus Care (AI triage)" },
  { value: "/(tabs)/account", label: "Account" },
];
export const PUSH_LINK_LABEL = (v) => (PUSH_LINKS.find((l) => l.value === (v || ""))?.label || v);
export const RADIOLOGY_STATUSES = ["REFERRED", "SCHEDULED", "COMPLETED", "REPORTED", "CANCELLED"];

// Reference range on TestMaster is stored as JSON: [{ low, high, gender?, ageMin?, ageMax?, pregnant?, text? }]
// Rich text (a TinyMCE range such as "<p>&lt;200 mg/dl - Desirable</p><p>200-239 ...</p>")
// as plain words: tags go, entities decode, paragraphs are joined with " · ".
export const htmlToText = (html) => {
  const s = String(html ?? "");
  if (!/<[a-z!/]|&[a-z#0-9]+;/i.test(s)) return s.trim();
  const broken = s.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n").replace(/<[^>]+>/g, "");
  let decoded = broken;
  if (typeof document !== "undefined") { const el = document.createElement("textarea"); el.innerHTML = broken; decoded = el.value; }
  else decoded = broken.replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&amp;/g, "&");
  return decoded.split(/\n+/).map((x) => x.replace(/\s+/g, " ").trim()).filter(Boolean).join(" · ");
};

export const fmtRange = (rr, unit) => {
  if (!rr) return "—";
  const arr = Array.isArray(rr) ? rr : [rr];
  const first = arr[0];
  if (!first) return "—";
  if (first.text && first.low == null && first.high == null) return htmlToText(first.text);
  const lo = first.low ?? first.min;
  const hi = first.high ?? first.max;
  if (lo == null && hi == null) return typeof first === "string" ? first : "—";
  return `${lo ?? "…"} – ${hi ?? "…"}${unit ? " " + unit : ""}${arr.length > 1 ? ` (+${arr.length - 1})` : ""}`;
};

export const rangeLabel = (r) =>
  [r.gender ? human(r.gender) : null, r.ageMin != null || r.ageMax != null ? `${r.ageMin ?? 0}–${r.ageMax ?? "∞"} y` : null, r.pregnant ? "pregnant" : null]
    .filter(Boolean)
    .join(" · ") || "Default";

export const patientAge = (p) => {
  if (!p) return "—";
  if (p.ageYears != null) return `${p.ageYears}y`;
  if (p.dob) {
    const y = Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25 * 24 * 3600 * 1000));
    return `${y}y`;
  }
  return "—";
};

export const genderShort = (g) => ({ MALE: "M", FEMALE: "F", OTHER: "O" }[g] || "—");

// "Serum / Plasma": a test's sample type plus the other sample types it accepts
// (altSampleTypeIds, named through the sample types master the page already loads).
export const sampleLabel = (t, sampleTypes = []) => {
  const main = t?.sampleType?.name || null;
  const byId = new Map((sampleTypes || []).map((s) => [s.id, s.name]));
  const alts = (t?.altSampleTypeIds || []).map((id) => byId.get(id)).filter((n) => n && n !== main);
  return [main, ...alts].filter(Boolean).join(" / ") || "—";
};

// Courtesy titles the desk may pick at registration; blank = worked out from sex and age, the
// same rule as the server's utils/patientTitle.js (adult Mr. / Ms., child Master / Miss, under a year Baby).
// The lab's full "Initial" list (2026-10-26); same list as Backend/src/utils/patientTitle.js.
export const TITLES = ["Mr.", "Mrs.", "Ms.", "Master", "Miss", "Baby", "Baby of", "Dr.", "Smt.", "Shri", "Kumari", "Brother", "Sister", "Father", "Vidvan", "Pandit", "Pet", "Vet"];
export const patientTitle = (p) => {
  if (!p) return "";
  if (p.title) return p.title;
  const years = p.dob ? (Date.now() - new Date(p.dob).getTime()) / (365.25 * 864e5) : p.ageYears != null ? Number(p.ageYears) : null;
  if (years != null && years < 1) return "Baby";
  const adult = years == null || years >= 18;
  return p.gender === "MALE" ? (adult ? "Mr." : "Master") : p.gender === "FEMALE" ? (adult ? "Ms." : "Miss") : "";
};

// Read a File object as text (CSV imports).
export const readFileText = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error);
    r.readAsText(file);
  });

// Read a File object as a data URL (signature images).
export const readFileDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });

export const BANNER_KINDS = ["BANNER", "SPOTLIGHT"];
export const BANNER_PLACEMENTS = [["HOME_TOP", "Home · top carousel"], ["HOME_SEARCH", "Home · under the search box (slim banner)"], ["HOME_MIDDLE", "Home · after packages"], ["HOME_END", "Home · bottom"], ["TEST_PAGE", "Tests page"], ["CHECKUP_PAGE", "Packages page"], ["HOME_HEADER", "Home · header background (photo or GIF for a day / festival)"], ["WEBSITE_HERO", "Website · home hero slider"], ["WEBSITE_BANNER", "Website · offer banners"]];
export const DISCOUNT_TYPES = ["PERCENT", "FIXED"];
export const COUPON_AUDIENCES = ["ALL", "NEW_PATIENTS", "EXISTING_PATIENTS", "SPECIFIC_PATIENT"];
export const OFFER_KINDS = ["PROMO", "APP_DISCOUNT", "FAMILY", "SENIOR", "VALUE", "SUPER", "INFLUENCER", "BIRTHDAY"];
export const CART_STATUSES = ["ACTIVE", "ABANDONED", "CONVERTED"];
export const PRESCRIPTION_STATUSES = ["PENDING", "APPROVED", "REJECTED"];
export const CAMPAIGN_STATUSES = ["DRAFT", "SCHEDULED", "SENT", "FAILED"];
export const ATTENDANCE_STATUSES = ["PRESENT", "ABSENT", "HALF_DAY", "LEAVE"];
export const TEST_CATEGORIES = ["HEART", "LIVER", "KIDNEY", "DIABETES", "THYROID", "VITAMINS", "WOMEN", "MEN", "PREGNANCY", "CANCER"];
export const TEMPLATE_KINDS = ["LAYOUT", "TEMPLATE"];

// ---- TAT and the promised report ---------------------------------------
// A test promises its report as a turnaround ("2 hr 30 min") counted from
// the sample collection or the scan; reportBy() adds it to the start time.
export const minutesToTime = (m) => (m == null || m === "" ? "" : `${String(Math.floor(Number(m) / 60)).padStart(2, "0")}:${String(Number(m) % 60).padStart(2, "0")}`);
export const timeToMinutes = (t) => { if (!t) return null; const [h, m] = String(t).split(":").map(Number); return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null; };
export function reportBy(registeredAt, test) {
  if (!registeredAt || !test) return null;
  const base = new Date(registeredAt);
  if (Number.isNaN(base.getTime())) return null;
  const mins = (Number(test.tatHours) || 0) * 60 + (Number(test.tatMinutes) || 0);
  return mins > 0 ? new Date(base.getTime() + mins * 60000) : null;
}
export const tatLabel = (test) => {
  if (!test) return "";
  const h = Number(test.tatHours) || 0, m = Number(test.tatMinutes) || 0;
  if (!h && !m) return "";
  // whole days read as days (168 hr -> 7 days), as the TAT boxes offer (2026-10-24)
  if (!m && h >= 24 && h % 24 === 0) return `${h / 24} day${h === 24 ? "" : "s"}`;
  return `${h ? `${h} hr` : ""}${h && m ? " " : ""}${m ? `${m} min` : ""}`;
};
